import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import { Modal } from '@heroui/react';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { useGuildRoster } from '@/lib/useGuildRoster';
import type { UserSearchResult } from '@bunker/shared/types';
import {
  HashIcon,
  UserIcon,
  UsersIcon,
  VolumeIcon,
} from '@/components/icons';
import { onAppEvent } from '@/lib/appEvents';

/**
 * Cmd/Ctrl+K palette — Discord's killer feature, smallest unit of "feels
 * fast". Type a few characters, hit Enter, you're in the right channel /
 * DM / guild without touching the mouse.
 *
 * Result types:
 *   - Guild:   open it, leave channel selection to user
 *   - Channel: open the guild that owns it, then the channel
 *   - DM:      open the existing direct channel
 *
 * We rank prefix matches above substring matches and break ties on
 * recency-ish weight (current guild's channels float up). Fuzzy match
 * (subsequence) is intentionally NOT implemented — for chat targets the
 * fan-out from fuzzy gets noisy; users prefer "I typed `gen`, I see
 * #general" deterministically.
 */
export function QuickSwitcher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Global Ctrl/Cmd+K binding. Attached at the document level so it works
  // no matter where focus is (composer, channel list, profile popover).
  // Also listens for a 'quickswitcher:open' custom event so any UI button
  // (e.g. the drawer "поиск" pill) can pop the palette without prop drilling.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!e.defaultPrevented && !e.isComposing && !e.shiftKey && !e.altKey && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    document.addEventListener('keydown', onKey);
    const unsubOpen = onAppEvent('quickswitcher:open', () => setOpen(true));
    return () => {
      document.removeEventListener('keydown', onKey);
      unsubOpen();
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setQuery('');
  }, [open]);

  const results = useQuickSwitcherResults(query, open);

  function commit(key: string) {
    const entry = results.find((r) => r.key === key);
    if (!entry) return;
    entry.activate();
    setOpen(false);
  }

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={setOpen}>
      <Modal.Container size="lg" placement="top">
        <Modal.Dialog aria-label={t('ui.quickSwitcher.title')} className="overflow-hidden p-0">
          <Command
            // We do our own scoring (recency + guild boost + prefix-over-
            // substring). Tell cmdk to render every item we feed and skip
            // its built-in fuzzy filter.
            shouldFilter={false}
            label={t('ui.quickSwitcher.title')}
          >
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={t('ui.quickSwitcher.placeholder')}
              className="block w-full bg-transparent px-5 py-4 text-base text-ink placeholder:text-ink-muted focus:outline-none"
              autoFocus
            />
            <div className="border-t border-separator" />
            <Command.List className="scrollbar-thin max-h-[50vh] overflow-y-auto py-1">
              {query && (
                <Command.Empty className="px-5 py-6 text-center text-xs text-ink-muted">
                  {t('ui.quickSwitcher.emptyQuery')}
                </Command.Empty>
              )}
              {results.map((entry) => (
                <Command.Item
                  key={entry.key}
                  value={`${entry.label} ${entry.sublabel} ${entry.kindLabel}`}
                  onSelect={() => commit(entry.key)}
                  className={
                    'group relative flex w-full cursor-pointer items-center gap-3 px-5 py-2.5 text-left transition-colors ' +
                    'data-[selected=true]:bg-accent/8 ' +
                    'data-[selected=true]:before:absolute data-[selected=true]:before:left-0 ' +
                    'data-[selected=true]:before:top-2 data-[selected=true]:before:bottom-2 ' +
                    'data-[selected=true]:before:w-0.5 data-[selected=true]:before:rounded-r ' +
                    'data-[selected=true]:before:bg-accent'
                  }
                >
                  <span
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-muted"
                    style={{ backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }}
                  >
                    {entry.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-light tracking-[-0.015em] text-ink">
                      {entry.label}
                    </span>
                    <span className="block truncate text-[11px] text-ink-muted">
                      {entry.sublabel}
                    </span>
                  </span>
                  <span
                    className="font-medium shrink-0 rounded px-1.5 py-0.5 text-[10px] text-ink-muted"
                    style={{ backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }}
                  >
                    {entry.kindLabel}
                  </span>
                </Command.Item>
              ))}
            </Command.List>
            <div
              className="hidden items-center gap-3 px-5 py-2.5 text-[11px] text-ink-muted sm:flex"
              style={{
                backgroundColor: 'rgb(var(--surface-section) / var(--surface-section-alpha))',
                borderTop: '1px solid rgb(var(--ring-inset) / var(--ring-inset-alpha))',
              }}
            >
              <Hint k="↑↓">{t('ui.quickSwitcher.hintNavigate')}</Hint>
              <Hint k="↵">{t('ui.quickSwitcher.hintOpen')}</Hint>
              <Hint k="esc">{t('ui.quickSwitcher.hintClose')}</Hint>
              <span className="ml-auto opacity-60">⌘K / Ctrl+K</span>
            </div>
          </Command>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function Hint(props: { k: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1">
      <kbd
        className="rounded-lg px-2 py-0.5 font-mono text-[11px] text-ink-dim"
        style={{
          backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))',
          boxShadow: 'inset 0 0 0 1px rgb(var(--ring-input) / var(--ring-input-alpha))',
        }}
      >
        {props.k}
      </kbd>
      {props.children}
    </span>
  );
}

interface QuickResult {
  key: string;
  label: string;
  sublabel: string;
  kindLabel: string;
  icon: React.ReactNode;
  /** Match-score for sort: lower = higher in the list. */
  score: number;
  activate: () => void;
}

/**
 * Build & filter the result list. Scoring rules (lower = better):
 *   - prefix match on label / username:  0
 *   - prefix match on display name:      1
 *   - substring on label:                10
 *   - substring on username/display:     11
 *   - inside current guild:              -1 (small boost)
 *   - DM with someone you already chat:  -1
 *   - global platform user search:       50 (below local results)
 *
 * Items not matching the query are filtered out unless the query is empty
 * (in which case we show a "recents-ish" default of: current guild's
 * channels + DMs).
 */
function useGlobalUserSearch(query: string, open: boolean): UserSearchResult[] {
  const [results, setResults] = useState<UserSearchResult[]>([]);

  useEffect(() => {
    if (!open || query.trim().length < 1) {
      setResults([]);
      return;
    }
    // Abort the previous request on each new run (and on close/unmount) so a
    // slow earlier response can't overwrite a newer one with stale results.
    const ctrl = new AbortController();
    const id = setTimeout(() => {
      api
        .searchUsers(query.trim(), undefined, ctrl.signal)
        .then((res) => setResults(res.users))
        .catch((err: { name?: string }) => {
          if (err?.name === 'AbortError') return;
          console.warn('user search failed', err);
          setResults([]);
        });
    }, 250);
    return () => {
      clearTimeout(id);
      ctrl.abort();
    };
  }, [query, open]);

  return results;
}

function useQuickSwitcherResults(query: string, open: boolean): QuickResult[] {
  const { t } = useTranslation();
  const guilds = useDataStore((s) => s.guilds);
  const guildOrder = useDataStore((s) => s.guildOrder);
  const dmChannels = useDataStore((s) => s.dmChannels);
  const dmOrder = useDataStore((s) => s.dmOrder);
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  const selectGuild = useDataStore((s) => s.selectGuild);
  const selectChannel = useDataStore((s) => s.selectChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const me = useAuthStore((s) => s.user);
  // Members of the current guild — lets the palette open a DM by name.
  const roster = useGuildRoster(currentGuildId);
  // Global platform search — debounced 250 ms, returns users outside the guild.
  const globalUsers = useGlobalUserSearch(query, open);

  return useMemo(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    const out: QuickResult[] = [];

    // Helper: produce a (matched, score) tuple for a candidate label/handle.
    function rank(...names: (string | null | undefined)[]): {
      matched: boolean;
      score: number;
    } {
      if (!q) return { matched: true, score: 100 };
      let best = Infinity;
      for (let i = 0; i < names.length; i++) {
        const n = (names[i] ?? '').toLowerCase();
        if (!n) continue;
        if (n.startsWith(q)) best = Math.min(best, i);
        else if (n.includes(q)) best = Math.min(best, 10 + i);
      }
      return { matched: best !== Infinity, score: best };
    }

    // Guilds
    for (const gid of guildOrder) {
      const g = guilds[gid];
      if (!g) continue;
      const r = rank(g.name);
      if (!r.matched) continue;
      out.push({
        key: `g-${g.id}`,
        label: g.name,
        sublabel: t('ui.quickSwitcher.server'),
        kindLabel: t('ui.quickSwitcher.server'),
        icon: g.iconUrl ? (
          <img src={g.iconUrl} alt="" className="h-7 w-7 rounded-lg object-cover" />
        ) : (
          <span className="text-xs font-light tracking-[-0.015em] text-ink">
            {g.name.charAt(0).toUpperCase()}
          </span>
        ),
        score: r.score,
        activate: () => {
          selectGuild(g.id);
        },
      });
    }

    // Channels — TEXT/VOICE only (categories aren't navigable destinations)
    for (const gid of guildOrder) {
      const g = guilds[gid];
      if (!g) continue;
      const boost = g.id === currentGuildId ? -1 : 0;
      for (const ch of g.channels) {
        if (ch.type !== 'TEXT' && ch.type !== 'VOICE' && ch.type !== 'ANNOUNCE') continue;
        const r = rank(ch.name, g.name);
        if (!r.matched) continue;
        const Icon = ch.type === 'VOICE' ? VolumeIcon : HashIcon;
        out.push({
          key: `c-${ch.id}`,
          label: `#${ch.name ?? t('ui.quickSwitcher.unnamed')}`,
          sublabel: g.name,
          kindLabel: ch.type === 'VOICE' ? t('ui.quickSwitcher.voice') : t('ui.quickSwitcher.channel'),
          icon: <Icon className="h-4 w-4" />,
          score: r.score + boost,
          activate: () => {
            if (g.id !== currentGuildId) selectGuild(g.id);
            selectChannel(ch.id);
          },
        });
      }
    }

    // Direct channels (DM + GROUP_DM)
    for (const did of dmOrder) {
      const ch = dmChannels[did];
      if (!ch) continue;
      const other = ch.directParticipants.find((p) => p.userId !== me?.id);
      const namePieces = ch.directParticipants
        .filter((p) => p.userId !== me?.id)
        .map((p) => p.user.displayName || p.user.username);
      const label =
        ch.type === 'GROUP_DM'
          ? ch.name || namePieces.slice(0, 3).join(', ') || t('ui.quickSwitcher.group')
          : other?.user.displayName || other?.user.username || t('ui.quickSwitcher.dialog');
      const r = rank(label, other?.user.username, ...namePieces);
      if (!r.matched) continue;
      out.push({
        key: `d-${ch.id}`,
        label,
        sublabel:
          ch.type === 'GROUP_DM'
            ? t('ui.quickSwitcher.groupChatSub', { count: ch.directParticipants.length })
            : other?.user.username
              ? `@${other.user.username}`
              : t('ui.quickSwitcher.directMessages'),
        kindLabel: ch.type === 'GROUP_DM' ? t('ui.quickSwitcher.group') : t('ui.quickSwitcher.dm'),
        icon: other?.user.avatarUrl ? (
          <img
            src={other.user.avatarUrl}
            alt=""
            className="h-7 w-7 rounded-full object-cover"
          />
        ) : ch.type === 'GROUP_DM' ? (
          <UsersIcon className="h-4 w-4" />
        ) : (
          <span className="text-xs font-light tracking-[-0.015em] text-ink">
            {label.charAt(0).toUpperCase()}
          </span>
        ),
        score: r.score - 1,
        activate: () => {
          selectDirectChannel(ch.id);
        },
      });
    }

    // Guild members of the CURRENT guild — "open DM with X by name". Only
    // when the user typed something (an unfiltered member dump would bury the
    // channel/DM results). Members who already have a 1:1 DM are skipped — they
    // surface above as a DM destination, so this avoids a duplicate row.
    if (q && me) {
      const dmPeerIds = new Set<string>();
      for (const did of dmOrder) {
        const ch = dmChannels[did];
        if (ch?.type === 'DM') {
          const peer = ch.directParticipants.find((p) => p.userId !== me.id);
          if (peer) dmPeerIds.add(peer.userId);
        }
      }
      for (const m of roster.members) {
        if (m.userId === me.id || dmPeerIds.has(m.userId)) continue;
        const label = m.nickname || m.user.displayName || m.user.username;
        const r = rank(label, m.user.username, m.user.displayName);
        if (!r.matched) continue;
        out.push({
          key: `m-${m.userId}`,
          label,
          sublabel: t('ui.quickSwitcher.openDmSub', { username: m.user.username }),
          kindLabel: t('ui.quickSwitcher.member'),
          icon: m.user.avatarUrl ? (
            <img src={m.user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <UserIcon className="h-4 w-4" />
          ),
          // Slightly below channels/DMs so concrete destinations win ties.
          score: r.score + 5,
          activate: () => {
            void (async () => {
              try {
                const channel = await api.openDirectMessage(m.userId);
                upsertDmChannel(channel);
                selectDirectChannel(channel.id);
              } catch (err) {
                toast.error(...errToToast(err, t('ui.quickSwitcher.openDmFailed')));
              }
            })();
          },
        });
      }
    }

    // Global platform users — shown when the query is non-empty, deduped
    // against guild members and existing DM peers (they already appear above).
    if (q && me) {
      const existingUserIds = new Set<string>(out.map((r) => r.key.startsWith('m-') ? r.key.slice(2) : ''));
      // Also cover DM peers already in the list.
      for (const did of dmOrder) {
        const ch = dmChannels[did];
        if (ch?.type === 'DM') {
          const peer = ch.directParticipants.find((p) => p.userId !== me.id);
          if (peer) existingUserIds.add(peer.userId);
        }
      }
      for (const u of globalUsers) {
        if (u.id === me.id || existingUserIds.has(u.id)) continue;
        const name = u.displayName || u.username;
        out.push({
          key: `gu-${u.id}`,
          label: name,
          sublabel: t('ui.quickSwitcher.globalUserSub', { username: u.username }),
          kindLabel: t('ui.quickSwitcher.globalUser'),
          icon: u.avatarUrl ? (
            <img src={u.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <UserIcon className="h-4 w-4" />
          ),
          // Below local results so local context wins ties.
          score: 50,
          activate: () => {
            void (async () => {
              try {
                const channel = await api.openDirectMessage(u.id);
                upsertDmChannel(channel);
                selectDirectChannel(channel.id);
              } catch (err) {
                toast.error(...errToToast(err, t('ui.quickSwitcher.openDmFailed')));
              }
            })();
          },
        });
      }
    }

    out.sort((a, b) => a.score - b.score);
    return out.slice(0, 30);
  }, [
    open,
    query,
    guilds,
    guildOrder,
    dmChannels,
    dmOrder,
    currentGuildId,
    me,
    roster.members,
    globalUsers,
    selectGuild,
    selectChannel,
    selectDirectChannel,
    upsertDmChannel,
    t,
  ]);
}
